/**
 * OrgSwitcherDialog — multi-org selector for users with access to >1 org.
 *
 * Wires into ``UserMenuButton``'s "Cambia organizzazione" row. The BE
 * endpoints (``GET /users/me/orgs`` + ``POST /users/me/active-org``) are
 * still pending; until they land, the dialog falls back to the local
 * session (a single-row view of the current org) so the action isn't a
 * dead-end. Once the BE ships, swap the local fallback for the real list
 * and the switch mutation — file-local TODO + typed banner mark the gap.
 *
 * UX (UI/UX rubric May 2026):
 *  - Search input top, debounced 250ms, focused on open.
 *  - Current org pinned + "Attiva" badge. Member count tabular-nums.
 *  - Row click → confirm + POST + hard reload (org context affects every
 *    cached query — cleanest reset).
 *  - Keyboard: ESC close, ↑/↓ navigate, Enter switch, Cmd+K focus search.
 *  - WCAG 4.5:1, focus rings, 44pt touch.
 */

import { Check, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";

import { getSession } from "@ando/core/auth";
import { Dialog, DialogContent, cn } from "@ando/ui";

export interface OrgSummary {
  readonly org_id: string;
  readonly name: string;
  readonly role?: string;
  readonly member_count?: number;
  readonly is_active?: boolean;
}

export interface OrgSwitcherDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Stub for the upstream BE endpoint. Returns the current org from the
 * session so the UI degrades gracefully when the multi-org list isn't
 * yet exposed. Once ``getMyOrgs`` ships in ``@ando/core``, replace the
 * inline implementation here with a one-line import.
 */
async function fetchMyOrgs(
  fallbackOrgName: string,
): Promise<{ orgs: OrgSummary[]; gap: boolean }> {
  const session = getSession();
  const currentOrgId = session?.org_id ?? "";
  // TODO BE: ``GET /api/v1/users/me/orgs`` (Stream G round 3). Until then,
  // render a single-row list so the action isn't a 404 — the banner inside
  // ``OrgSwitcherDialog`` signals the limitation.
  return {
    orgs: currentOrgId
      ? [
          {
            org_id: currentOrgId,
            name: fallbackOrgName,
            is_active: true,
          },
        ]
      : [],
    gap: true,
  };
}

export function OrgSwitcherDialog({
  open,
  onOpenChange,
}: OrgSwitcherDialogProps): React.ReactElement | null {
  const { i18n, t } = useLingui();
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const searchRef = useRef<HTMLInputElement | null>(null);

  // Debounce search input by 250ms so filtering doesn't thrash on key-by-key.
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQuery(query.trim().toLowerCase()), 250);
    return () => window.clearTimeout(t);
  }, [query]);

  // Autofocus search on open.
  useEffect(() => {
    if (open) {
      const id = window.requestAnimationFrame(() => searchRef.current?.focus());
      return () => window.cancelAnimationFrame(id);
    }
    setQuery("");
    setDebouncedQuery("");
    setHighlighted(0);
  }, [open]);

  const q = useQuery({
    queryKey: ["users", "me", "orgs"],
    queryFn: () => fetchMyOrgs(t`La tua organizzazione`),
    enabled: open,
    staleTime: 60_000,
  });

  const filtered = useMemo(() => {
    const list = q.data?.orgs ?? [];
    if (!debouncedQuery) return list;
    return list.filter((o) => o.name.toLowerCase().includes(debouncedQuery));
  }, [q.data, debouncedQuery]);

  // Keep highlighted index inside bounds on filter changes.
  // Strict-bounds check + ``highlighted`` is NOT a dep — otherwise the
  // setState callback re-triggers the effect after each commit and
  // React 19 trips ``Maximum update depth exceeded`` (#185). The
  // functional ``setHighlighted(prev => …)`` form below reads the
  // freshest index without subscribing to it.
  useEffect(() => {
    setHighlighted((prev) => {
      if (filtered.length === 0) return 0;
      if (prev >= filtered.length) return filtered.length - 1;
      return prev;
    });
  }, [filtered.length]);

  const switchTo = async (org: OrgSummary): Promise<void> => {
    if (org.is_active) {
      onOpenChange(false);
      return;
    }
    // TODO BE: ``POST /api/v1/users/me/active-org`` (Stream G round 3).
    // Once shipped, replace this with the mutation. Hard reload after
    // success is intentional — org context invalidates every cached
    // query (capabilities, projects, conversations, billing…).
    // For now, surface a no-op + close so the UI doesn't lie.
    console.info("[OrgSwitcher] BE endpoint pending — no-op switch", org.org_id);
    onOpenChange(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      const target = filtered[highlighted];
      if (target) {
        e.preventDefault();
        void switchTo(target);
      }
    } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
      e.preventDefault();
      searchRef.current?.focus();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[480px] gap-0 p-0"
        ariaTitle={i18n._(msg`Cambia organizzazione`)}
        onKeyDown={onKeyDown}
      >
        <header className="border-hairline border-b px-4 py-3">
          <h2 className="font-semibold text-[14px] text-ink-1"><Trans>Cambia organizzazione</Trans></h2>
          <p className="mt-0.5 text-[11px] text-ink-3">
            <Trans>Seleziona l'organizzazione attiva.</Trans>
          </p>
        </header>

        <div className="px-3 pt-3">
          <label className="relative block">
            <Search
              className="-translate-y-1/2 absolute top-1/2 left-2.5 h-3.5 w-3.5 text-ink-3"
              aria-hidden
            />
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={i18n._(msg`Cerca…`)}
              aria-label={i18n._(msg`Cerca organizzazione`)}
              className={cn(
                "h-9 w-full rounded-md border border-hairline bg-card px-3 pl-8 text-[12px] text-ink-1",
                "placeholder:text-ink-3",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-1",
              )}
            />
          </label>
        </div>

        {q.data?.gap ? (
          <div className="mx-3 mt-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-[11px] text-amber-900">
            <Trans>
              La lista completa delle organizzazioni arriverà a breve. Per ora vedi
              solo l'organizzazione attiva.
            </Trans>
          </div>
        ) : null}

        <ul aria-label={i18n._(msg`Organizzazioni`)} className="max-h-[320px] overflow-y-auto p-2">
          {filtered.length === 0 ? (
            <li className="px-2 py-6 text-center text-[12px] text-ink-3">
              <Trans>Nessuna organizzazione corrisponde.</Trans>
            </li>
          ) : (
            filtered.map((org, i) => {
              const active = i === highlighted;
              return (
                <li key={org.org_id} aria-selected={active}>
                  <button
                    type="button"
                    onClick={() => void switchTo(org)}
                    onMouseEnter={() => setHighlighted(i)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-left transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-1",
                      active ? "bg-cream-50" : "hover:bg-cream-50/60",
                    )}
                  >
                    <span
                      aria-hidden
                      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-ando-orange-light to-ando-orange font-semibold text-[12px] text-white"
                    >
                      {org.name.trim().charAt(0).toUpperCase() || "?"}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-[12px] text-ink-1">
                        {org.name}
                      </span>
                      <span className="block truncate text-[10px] text-ink-3 tabular-nums">
                        {org.role ?? "—"}
                        {typeof org.member_count === "number"
                          ? ` · ${t`${org.member_count} membri`}`
                          : ""}
                      </span>
                    </span>
                    {org.is_active ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-ando-orange-soft px-1.5 py-0.5 font-semibold text-[10px] text-ando-orange uppercase tracking-wide">
                        <Check className="h-2.5 w-2.5" aria-hidden />
                        <Trans>Attiva</Trans>
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })
          )}
        </ul>

        <footer className="border-hairline border-t px-4 py-2 text-[10px] text-ink-3">
          <span className="font-mono">↑↓</span> <Trans>per navigare</Trans> ·{" "}
          <span className="font-mono">↵</span> <Trans>per selezionare</Trans> ·{" "}
          <span className="font-mono">ESC</span> <Trans>per chiudere</Trans>
        </footer>
      </DialogContent>
    </Dialog>
  );
}
OrgSwitcherDialog.displayName = "OrgSwitcherDialog";
